import { ExternalLink } from 'lucide-react';
import { rd, type RemoteData } from '@passionware/monads';
import { promiseState } from '@passionware/platform-react';
import { useCallback, useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Link } from 'react-router-dom';
import type { Production, Resource } from '__types__';
import { MissingBookingConfirmationTemplateError } from 'v5/api/booking/booking.api';
import { extractBookingIdsFromProduction } from 'v5/api/production-resourcing/extractBookingIdsFromProduction';
import type { AppScope } from 'v5/core/app-scope/appScope.types';
import { Button } from 'v5/design-system/Button';
import {
  Dialog,
  DialogBody,
  DialogContentHeadless,
  DialogHeader,
  DialogTitle
} from 'v5/design-system/Dialog';
import { Skeleton } from 'v5/design-system/Skeleton';
import { MountElementProps } from 'v5/messages/mount-element';
import { InfoCallout } from 'v5/patterns/feedback/InfoCallout';
import { FormDialogContent } from 'v5/patterns/forms/dialogs/FormDialogContent';
import { AsyncButton } from 'v5/patterns/forms/fields/AsyncButton';
import { getUserFacingErrorMessage } from 'v5/platform/lang/getUserFacingErrorMessage';
import { WithServices } from 'v5/platform/ts/services';
import { WithToastService } from 'v5/services/front/ToastService/ToastService';
import { WithSystemSettingsService } from 'v5/services/internal/SystemSettingsService/SystemSettingsService';
import { WithBookingMutationService } from 'v5/services/io/BookingMutationService/BookingMutationService';
import { WithBookingService } from 'v5/services/io/BookingService/BookingService';
import { WithEnterpriseService } from 'v5/services/io/EnterpriseService/EnterpriseService';
import { WithProductionService } from 'v5/services/io/ProductionService/ProductionService';
import { WithResourceService } from 'v5/services/io/ResourceService/ResourceService';
import { SendBookingConfirmationBulkBody } from './SendBookingConfirmationBulkBody';
import {
  type ConfirmationRecipient,
  type ConfirmationRecipientList,
  recipientEmailsFromResource,
  useProductionConfirmationRecipients
} from './useProductionConfirmationRecipients';
import { useConfirmationPreview } from './useConfirmationPreview';

type SendBookingConfirmationDialogBaseProps = MountElementProps &
  WithServices<
    [
      WithToastService,
      WithSystemSettingsService,
      WithEnterpriseService,
      WithBookingService,
      WithBookingMutationService,
      WithResourceService,
      WithProductionService
    ]
  > & {
    appScope: AppScope;
  };

export type SendBookingConfirmationDialogProps =
  SendBookingConfirmationDialogBaseProps &
    ({ bookingId: number } | { productionId: number });

function confirmationListFromResource(
  bookingId: number,
  resource: Resource
): ConfirmationRecipientList {
  const emails = recipientEmailsFromResource(resource);
  const row: ConfirmationRecipient = {
    bookingId,
    contactId: resource.id,
    resource,
    emails
  };
  return emails.length === 0
    ? { ready: [], problems: [{ ...row, problem: 'missing_email' }] }
    : { ready: [row], problems: [] };
}

export function SendBookingConfirmationDialog(
  props: SendBookingConfirmationDialogProps
) {
  const { open, onOpenChange, close, appScope, services } = props;
  const isBulk = 'productionId' in props;
  const bookingId = 'bookingId' in props ? props.bookingId : undefined;
  const productionId = 'productionId' in props ? props.productionId : undefined;

  const { t } = useTranslation('productions', {
    keyPrefix: 'sendBookingConfirmationDialog'
  });

  const organizationId = appScope.organizationId;

  const productionRd = services.productionService.useProduction(
    productionId,
    organizationId
  );
  const bookingIdsFromProductionRd = rd.useMemoMap(
    productionRd,
    extractBookingIdsFromProduction
  );
  const bookingIdsFromProduction = rd.getOrElse(
    bookingIdsFromProductionRd,
    [] as number[]
  );

  const bookingRd = services.bookingService.useBooking(bookingId);
  const resourceRd = services.resourceService.useItem(
    rd.tryMap(bookingRd, booking => booking.contact_id)
  );

  const recipientsRd = useProductionConfirmationRecipients(
    isBulk ? bookingIdsFromProduction : [],
    services
  );
  const recipientsList: ConfirmationRecipientList = rd.tryGet(recipientsRd) ?? {
    ready: [],
    problems: []
  };
  const [previewIndex, setPreviewIndex] = useState(0);

  useEffect(() => {
    setPreviewIndex(0);
  }, [bookingId, productionId, open]);

  useEffect(() => {
    if (recipientsList.ready.length === 0) {
      setPreviewIndex(0);
      return;
    }
    setPreviewIndex(index => Math.min(index, recipientsList.ready.length - 1));
  }, [recipientsList.ready.length]);

  const { previewRequest, previewRd, templateScopeRd } = useConfirmationPreview(
    {
      open,
      appScope,
      target: isBulk
        ? {
            kind: 'production',
            recipientsRd,
            selectedIndex: previewIndex
          }
        : { kind: 'booking', bookingRd, resourceRd }
    },
    services
  );
  const previewContentRd = rd.useMemoMap(previewRd, preview => preview.content);
  const templatesPath = rd.tryGet(templateScopeRd)?.templatesPath ?? null;

  const sendMutation = promiseState.useMutation(async () => {
    try {
      if (isBulk) {
        if (productionId == null || recipientsList.ready.length === 0) {
          return;
        }
        await services.bookingMutationService.sendBookingConfirmations({
          productionId,
          enterpriseWorkspaceId: previewRequest?.enterpriseWorkspaceId,
          affectedBookingIds: recipientsList.ready.map(
            recipient => recipient.bookingId
          )
        });
      } else {
        if (bookingId == null) {
          return;
        }
        await services.bookingMutationService.sendBookingConfirmations({
          bookingId,
          enterpriseWorkspaceId: previewRequest?.enterpriseWorkspaceId
        });
      }
      close();
      services.toastService.open({
        type: 'success',
        message: isBulk ? t('sendSuccessBulk') : t('sendSuccess')
      });
    } catch (error) {
      if (error instanceof MissingBookingConfirmationTemplateError) {
        services.toastService.open({
          type: 'error',
          message: t('missingTemplateDescription')
        });
        return;
      }
      services.toastService.open({
        type: 'error',
        message: getUserFacingErrorMessage(error) || t('sendFailed')
      });
      throw error;
    }
  });

  const handleOpenChange = useCallback(
    (next: boolean) => {
      onOpenChange(next);
      if (!next) {
        close();
      }
    },
    [close, onOpenChange]
  );

  const scopeBannerRd = rd.map(templateScopeRd, scope =>
    scope.workspaceId == null
      ? {
          title: t('scopeOrganizationTitle'),
          description: t('scopeOrganizationDescription')
        }
      : {
          title: t('scopeWorkspaceTitle', {
            workspace: scope.workspace?.name ?? t('scopeWorkspaceFallback')
          }),
          description:
            scope.origin === 'global'
              ? t('scopeWorkspaceFallbackToGlobal')
              : t('scopeWorkspaceDescription')
        }
  );

  const previewHtml = rd.tryGet(previewContentRd);
  const canSend = previewRequest != null && !!previewHtml?.trim();

  const dialogTitle = isBulk ? t('titleBulk') : t('title');
  const dialogDescription = isBulk ? t('descriptionBulk') : t('description');

  return (
    <Dialog open={open} onOpenChange={handleOpenChange}>
      <DialogContentHeadless>
        <FormDialogContent
          open={open}
          title={dialogTitle}
          description={dialogDescription}
          className="max-h-[min(92vh,72rem)] w-[min(92vw,50rem)] max-w-200"
          onSubmit={() => {
            if (!canSend) return;
            sendMutation.track(undefined);
          }}
          mutation={sendMutation.state}
          disabled={!canSend}
          saveText={t('send')}
          renderHeader={title => (
            <DialogHeader>
              <DialogTitle>{title}</DialogTitle>
            </DialogHeader>
          )}
          footerContent={
            <Button
              type="button"
              variant="secondary"
              onClick={() => handleOpenChange(false)}
            >
              {t('cancel')}
            </Button>
          }
          saveButtonSlot={
            <AsyncButton
              type="submit"
              variant="primaryAlt"
              disabled={!canSend}
              mutation={sendMutation.state}
            >
              {t('send')}
            </AsyncButton>
          }
          renderDialogBody={() => (
            <DialogBody
              className="flex min-h-0 flex-1 flex-col gap-4 overflow-hidden"
              renderTopShadow={false}
              renderBottomShadow={false}
            >
              {rd
                .journey(scopeBannerRd)
                .wait(
                  // Wait for the preview to say which template it rendered rather than guess.
                  <InfoCallout className="shrink-0">
                    <div className="flex flex-col gap-1.5" aria-busy="true">
                      <Skeleton className="h-4 w-48" />
                      <Skeleton className="h-4 w-72" />
                    </div>
                  </InfoCallout>
                )
                .catch(() => null)
                .map(banner => (
                  <InfoCallout className="shrink-0">
                    <p className="font-medium text-fg-default">
                      {banner.title}
                    </p>
                    <p className="mt-0.5 text-fg-muted">{banner.description}</p>
                    {templatesPath ? (
                      <Link
                        to={templatesPath}
                        target="_blank"
                        rel="noopener noreferrer"
                        className="mt-1.5 inline-flex items-center gap-1 text-sm text-fg-accent hover:underline"
                      >
                        {t('editTemplateLink')}
                        <ExternalLink
                          className="size-3.5 shrink-0"
                          aria-hidden
                        />
                      </Link>
                    ) : null}
                  </InfoCallout>
                ))}

              {isBulk ? (
                <ProductionBulkBodyLoader
                  productionRd={productionRd}
                  recipientsRd={recipientsRd}
                  selectedIndex={previewIndex}
                  onSelectIndex={setPreviewIndex}
                  previewRd={previewContentRd}
                  templatesPath={templatesPath}
                />
              ) : (
                <div className="flex min-h-0 flex-1 flex-col">
                  {rd
                    .journey(bookingRd)
                    .wait(<Skeleton className="h-16 w-full" />)
                    .catch(() => (
                      <p className="text-sm text-fg-danger">
                        {t('templateLoadFailed')}
                      </p>
                    ))
                    .map(booking =>
                      booking.contact_id == null ? (
                        <p className="text-sm text-fg-danger">
                          {t('noEmailInline')}
                        </p>
                      ) : (
                        rd
                          .journey(resourceRd)
                          .wait(<Skeleton className="h-16 w-full" />)
                          .catch(() => (
                            <p className="text-sm text-fg-danger">
                              {t('templateLoadFailed')}
                            </p>
                          ))
                          .map(loadedResource => (
                            <SendBookingConfirmationBulkBody
                              list={confirmationListFromResource(
                                booking.id,
                                loadedResource
                              )}
                              selectedIndex={0}
                              onSelectIndex={() => undefined}
                              previewRd={previewContentRd}
                              templatesPath={templatesPath}
                            />
                          ))
                      )
                    )}
                </div>
              )}
            </DialogBody>
          )}
        />
      </DialogContentHeadless>
    </Dialog>
  );
}

function ProductionBulkBodyLoader({
  productionRd,
  recipientsRd,
  selectedIndex,
  onSelectIndex,
  previewRd,
  templatesPath
}: {
  productionRd: RemoteData<Production>;
  recipientsRd: RemoteData<ConfirmationRecipientList>;
  selectedIndex: number;
  onSelectIndex: (index: number) => void;
  previewRd: RemoteData<string>;
  templatesPath: string | null;
}) {
  const { t } = useTranslation('productions', {
    keyPrefix: 'sendBookingConfirmationDialog'
  });

  return (
    <div className="flex min-h-0 flex-1 flex-col">
      {rd
        .journey(productionRd)
        .wait(
          <div className="flex min-h-0 flex-1 flex-col gap-3">
            <Skeleton className="h-10 w-full shrink-0" />
            <Skeleton className="h-48 w-full" />
          </div>
        )
        .catch(() => (
          <p className="text-sm text-fg-danger">{t('recipientsLoadFailed')}</p>
        ))
        .map(() =>
          rd
            .journey(recipientsRd)
            .wait(
              <div className="flex min-h-0 flex-1 flex-col gap-3">
                <Skeleton className="h-10 w-full shrink-0" />
                <Skeleton className="h-48 w-full" />
              </div>
            )
            .catch(() => (
              <p className="text-sm text-fg-danger">
                {t('recipientsLoadFailed')}
              </p>
            ))
            .map(list => (
              <SendBookingConfirmationBulkBody
                list={list}
                selectedIndex={selectedIndex}
                onSelectIndex={onSelectIndex}
                previewRd={previewRd}
                templatesPath={templatesPath}
              />
            ))
        )}
    </div>
  );
}
